<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useAIPlan } from '@/features/ai-plan/composables/useAIPlan'
import type { PlanData, PlanDay } from '@/features/ai-plan/lib/types'
import type { PregnancyPhase } from '@/features/ai-plan/lib/pregnancyPrompt'
import { computeExercise, type ComputableExercise } from '@/features/training/lib/computeExercise'
import { computeDayProgress, dayDoneKey, exerciseKey, phaseKey, type PhaseName } from '@/features/training/lib/dayProgress'
import { useExerciseNotes } from '@/features/training/composables/useExerciseNotes'
import type { NoteTarget } from '@/features/training/lib/exerciseNotes'
import NoteField from '@/features/training/components/NoteField.vue'
import WeekView from '@/features/training/components/WeekView.vue'
import { isoToYMD } from '@/shared/utils/dateMath'
import { getWeekRows, positionForDate } from '@/features/training/lib/weekView'
import type { CheckedMap, DayPosition } from '@/shared/bridge/trainingBridge'
import {
  clearActivePlan,
  emitPlanChanged,
  getActivePlan,
  getChecked,
  getDay,
  markDayDone,
  markPhaseDone,
  setActivePlan,
  startRestTimer,
  toggleExercise,
} from '@/shared/bridge/trainingAdapters'

type TrainingView = 'empty' | 'review' | 'active'
type PregnancySelection = 'none' | 'pregnancy' | PregnancyPhase

const importFileEl = ref<HTMLInputElement | null>(null)
const activePlan = ref<PlanData | null>(getActivePlan())
const view = ref<TrainingView>(activePlan.value ? 'active' : 'empty')
const importError = ref('')
const activatedPlan = ref<unknown | null>(null)

const {
  testDate, planStartDate, focus, level, daysPerWeek, injuries, equipment,
  isPregnancyMode, pregnancyPhase,
  hasScore, status, isLoading, planData, planSummary, errorMsg, currentModel,
  generate,
} = useAIPlan()

const pregnancySelection = computed<PregnancySelection>({
  get: () => {
    if (!isPregnancyMode.value) return 'none'
    return pregnancyPhase.value || 'pregnancy'
  },
  set: (value: PregnancySelection) => {
    isPregnancyMode.value = value !== 'none'
    pregnancyPhase.value = value === 'none' || value === 'pregnancy' ? '' : value
  },
})

// Current day position — reactive + settable (Phase 6). Seeded from the first
// incomplete training day; Week View navigation will call setPosition().
const todayISO = ref(isoToYMD(new Date()))

function initialPosition(): DayPosition {
  const plan = activePlan.value
  return plan ? positionForDate(plan, todayISO.value, getChecked()) : { week: 1, dayOfWeek: 1 }
}
const position = ref<DayPosition>(initialPosition())
function setPosition(week: number, dayOfWeek: number) { position.value = { week, dayOfWeek } }
const dayOne = computed<PlanDay | null>(() => getDay(activePlan.value, position.value))
const selectedRow = computed(() => {
  const plan = activePlan.value
  if (!plan) return null
  return getWeekRows(plan, position.value.week, checked.value, todayISO.value)
    .find(row => row.dayOfWeek === position.value.dayOfWeek) ?? null
})
const selectedDate = computed(() => selectedRow.value?.date ?? '')
const selectedDateLabel = computed(() => {
  if (!selectedDate.value) return ''
  const date = new Date(`${selectedDate.value}T00:00:00`)
  return Number.isNaN(date.getTime())
    ? selectedDate.value
    : date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
})
const selectedIsFuture = computed(() => !!selectedDate.value && selectedDate.value > todayISO.value)
const isRestDay = computed(() => dayOne.value?.is_rest === true)
const totalWeeks = computed(() => activePlan.value?.total_weeks || 1)
const phaseStartWeek = computed(() =>
  position.value.week > Math.ceil(totalWeeks.value / 2) && Array.isArray(activePlan.value?.week_template_phase2)
    ? Math.ceil(totalWeeks.value / 2) + 1
    : 1,
)

const warmupDrills = computed(() => dayOne.value?.warmup ?? [])
const cooldownStretches = computed(() => dayOne.value?.cooldown ?? [])
// Notes (Phase 5) — local-only, standalone composable (outside completion).
const { getNote, setNote } = useExerciseNotes()

const mainRows = computed(() =>
  (dayOne.value?.exercises ?? []).map((ex, index) => {
    const c = computeExercise(ex as ComputableExercise, position.value.week, totalWeeks.value, phaseStartWeek.value)
    return {
      index,
      name: c.name,
      detail: c.is_cardio_single ? String(c.reps_display) : `${c.computed_sets} × ${c.reps_display}`,
      restSeconds: typeof ex.rest_seconds === 'number' && ex.rest_seconds > 0 ? ex.rest_seconds : 0,
      sets: c.computed_sets,
      note: getNote({ kind: 'exercise', name: c.name }),
    }
  }),
)

function noteFor(target: NoteTarget): string { return getNote(target) }
function saveNote(target: NoteTarget, text: string): void { setNote(target, text) }

// Completion state — reactive copy of af_pt_plan_checked, refreshed on mutate.
const checked = ref<CheckedMap>(getChecked())
function refreshChecked() { checked.value = getChecked() }
const dayCompletePulse = ref(false)
let dayCompleteTimer: number | undefined

const warmupDone = computed(() => checked.value[phaseKey(position.value, 'warmup')] === true)
const cooldownDone = computed(() => checked.value[phaseKey(position.value, 'cooldown')] === true)
const dayDone = computed(() => checked.value[dayDoneKey(position.value)] === true)
const dayProgress = computed(() => computeDayProgress(dayOne.value, position.value, checked.value))
function isExerciseDone(index: number): boolean {
  return checked.value[exerciseKey(position.value, index)] === true
}

function onToggleExercise(index: number) {
  if (selectedIsFuture.value) return
  checked.value = toggleExercise(position.value, index)
}
function onMarkPhase(phase: PhaseName) {
  if (selectedIsFuture.value) return
  checked.value = markPhaseDone(position.value, phase)
}
function onMarkDay() {
  if (selectedIsFuture.value) return
  const wasDone = dayDone.value
  checked.value = markDayDone(position.value)
  if (!wasDone && checked.value[dayDoneKey(position.value)] === true) {
    dayCompletePulse.value = false
    window.clearTimeout(dayCompleteTimer)
    requestAnimationFrame(() => { dayCompletePulse.value = true })
    dayCompleteTimer = window.setTimeout(() => { dayCompletePulse.value = false }, 850)
  }
}
function onStartRest(seconds: number, sets: number) {
  if (selectedIsFuture.value) return
  startRestTimer(seconds, 'Rest', sets)
}

const generateLabel = computed(() => {
  if (!isLoading.value) return 'Generate Plan'
  return currentModel.value ? `Generating (${currentModel.value})` : 'Generating'
})

const aiBuildStages = ['Score profile', 'Workload', 'Weekly plan']

const daysUntilTest = computed<number | null>(() => {
  if (!testDate.value) return null
  return Math.max(0, Math.round((new Date(testDate.value).getTime() - Date.now()) / 86400000))
})

function tomorrowISO(): string {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  return isoToYMD(date)
}

function formatShortDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const planStartMin = computed(() => tomorrowISO())
const planStartRange = computed(() => {
  if (!planStartDate.value) return ''
  const start = new Date(`${planStartDate.value}T00:00:00`)
  if (Number.isNaN(start.getTime())) return ''
  const isoDay = ((start.getDay() + 6) % 7) + 1
  const end = new Date(start)
  end.setDate(start.getDate() + (7 - isoDay))
  return `Week 1: ${formatShortDate(planStartDate.value)} - ${formatShortDate(isoToYMD(end))}`
})

function refreshActivePlan() {
  activePlan.value = getActivePlan()
  if (activePlan.value) view.value = 'active'
  else if (view.value === 'active') view.value = 'empty'
}

function refreshTodayFromLocalClock() {
  const next = isoToYMD(new Date())
  const changed = next !== todayISO.value
  todayISO.value = next
  refreshChecked()
  if (changed && activePlan.value) {
    position.value = positionForDate(activePlan.value, next, checked.value)
  }
}

function openReview() {
  importError.value = ''
  if (!planStartDate.value || planStartDate.value < planStartMin.value) {
    planStartDate.value = planStartMin.value
  }
  view.value = 'review'
}

function backToEmpty() {
  view.value = activePlan.value ? 'active' : 'empty'
}

function onGenerate() {
  importError.value = ''
  void generate()
}

function activateGeneratedPlan(plan: unknown) {
  // useAIPlan.generate() has already persisted af_pt_active_plan, reset the
  // checked map, called window.setPlanData, and synced to Firestore. Avoid a
  // double write / double Firestore sync: just announce the change and refresh
  // from storage. (The import path still uses setActivePlan to persist.)
  emitPlanChanged({ source: 'generate' })
  activatedPlan.value = plan
  refreshActivePlan()
}

function clearPlan() {
  clearActivePlan()
  activePlan.value = null
  activatedPlan.value = null
  importError.value = ''
  view.value = 'empty'
}

function extractImportedPlan(value: unknown, depth = 0): unknown {
  if (depth > 8) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return value
    try {
      return extractImportedPlan(JSON.parse(trimmed), depth + 1)
    } catch {
      return value
    }
  }
  if (!value || typeof value !== 'object') return value

  const record = value as Record<string, unknown>
  if (Array.isArray(record.week_template)) return value

  for (const key of ['plan', 'training_plan', 'workout_plan', 'data', 'payload', 'currentPlanData']) {
    if (record[key]) {
      const candidate = extractImportedPlan(record[key], depth + 1)
      if (candidate && typeof candidate === 'object' && Array.isArray((candidate as Record<string, unknown>).week_template)) {
        return candidate
      }
    }
  }
  return value
}

function normalizeImportedPlan(value: unknown): PlanData {
  const candidate = extractImportedPlan(value)
  if (!candidate || typeof candidate !== 'object') throw new Error('Could not find a training plan in that file.')

  const plan = candidate as Partial<PlanData>
  if (!Array.isArray(plan.week_template) || plan.week_template.length === 0) {
    throw new Error('Imported plan is missing week_template.')
  }

  return {
    plan_name: plan.plan_name || 'Imported Training Plan',
    total_weeks: Number(plan.total_weeks) || 1,
    week_template: plan.week_template,
    week_template_phase2: Array.isArray(plan.week_template_phase2) ? plan.week_template_phase2 : undefined,
    phases: plan.phases,
    official_test_date: plan.official_test_date || '',
    generated_at: plan.generated_at || new Date().toISOString(),
    plan_start_date: plan.plan_start_date || undefined,
    _partial: plan._partial,
  }
}

async function handleImportFile(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  try {
    const parsed = JSON.parse(await file.text()) as unknown
    const plan = normalizeImportedPlan(parsed)
    setActivePlan(plan, 'import')
    window.showToast?.('Plan imported.')
    refreshActivePlan()
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Could not import plan JSON.'
    importError.value = message
    window.showToast?.(message)
  } finally {
    input.value = ''
  }
}

function onPlanChanged() {
  refreshActivePlan()
  refreshChecked()
  position.value = initialPosition()
}

function onAppMayHaveResumed() {
  if (document.visibilityState === 'hidden') return
  refreshTodayFromLocalClock()
}

function openReviewFromEvent() {
  refreshActivePlan()
  openReview()
}

watch(status, value => {
  if (value !== 'success' || !planData.value || activatedPlan.value === planData.value) return
  activateGeneratedPlan(planData.value)
})

let todayRefreshInterval: number | undefined

onMounted(() => {
  window.addEventListener('ptr:plan-changed', onPlanChanged)
  window.addEventListener('ptr:training-progress-changed', refreshChecked)
  window.addEventListener('ptr:open-training', openReviewFromEvent)
  window.addEventListener('ptr:update-plan-from-score', openReviewFromEvent)
  window.addEventListener('focus', onAppMayHaveResumed)
  document.addEventListener('visibilitychange', onAppMayHaveResumed)
  todayRefreshInterval = window.setInterval(refreshTodayFromLocalClock, 60_000)
})

onUnmounted(() => {
  window.removeEventListener('ptr:plan-changed', onPlanChanged)
  window.removeEventListener('ptr:training-progress-changed', refreshChecked)
  window.removeEventListener('ptr:open-training', openReviewFromEvent)
  window.removeEventListener('ptr:update-plan-from-score', openReviewFromEvent)
  window.removeEventListener('focus', onAppMayHaveResumed)
  document.removeEventListener('visibilitychange', onAppMayHaveResumed)
  window.clearInterval(todayRefreshInterval)
  window.clearTimeout(dayCompleteTimer)
})
</script>

<template>
  <div class="training-shell">
    <section v-if="view === 'active' && activePlan" class="card training-active">
      <div class="training-active__top">
        <div class="card-title">Training</div>
        <div class="training-active__date">{{ selectedDateLabel || `Week ${position.week}` }}</div>
      </div>

      <div class="training-day">
        <!-- Day progress -->
        <div class="training-progress" role="progressbar" :aria-valuenow="dayProgress.pct" aria-valuemin="0" aria-valuemax="100">
          <div class="training-progress__bar"><span :style="{ width: dayProgress.pct + '%' }" /></div>
          <div class="training-progress__text">{{ dayProgress.done }} / {{ dayProgress.total }} · {{ dayProgress.pct }}%</div>
        </div>

        <div v-if="selectedIsFuture" class="training-preview-note">Preview only. Logging unlocks on this date.</div>

        <!-- REST / RECOVERY DAY -->
        <div v-if="isRestDay" class="training-rest">
          <p class="training-rest__text">
            {{ dayOne?.session_purpose || 'Light cardio or brisk walking recommended (20–40 min at a conversational pace).' }}
          </p>
        </div>

        <!-- TRAINING DAY -->
        <template v-else>
          <!-- Warm-up phase -->
          <section v-if="warmupDrills.length" class="phase" :class="{ 'is-done': warmupDone }">
            <div class="phase__head">
              <h4>Warm-up</h4>
              <button type="button" class="phase__mark" :class="{ 'is-done': warmupDone }"
                      :aria-pressed="warmupDone" :disabled="selectedIsFuture" @click="onMarkPhase('warmup')">
                {{ warmupDone ? '✓ Complete' : 'Mark Phase Complete' }}
              </button>
            </div>
            <ul class="movement-list">
              <li v-for="(drill, i) in warmupDrills" :key="i">
                <span class="movement-list__name">{{ drill.name }}</span>
                <span v-if="drill.reps_display" class="movement-list__detail">{{ drill.reps_display }}</span>
              </li>
            </ul>
            <NoteField :text="noteFor({ kind: 'warmup' })" label="warm-up" :disabled="selectedIsFuture" @save="(t) => saveNote({ kind: 'warmup' }, t)" />
          </section>

          <!-- Main exercises -->
          <section class="phase">
            <div class="phase__head"><h4>Main</h4></div>
            <ul class="exercise-list">
              <li v-for="ex in mainRows" :key="ex.index" :class="{ 'is-done': isExerciseDone(ex.index) }">
                <div class="ex-row">
                  <button type="button" class="ex-check" :class="{ 'is-done': isExerciseDone(ex.index) }"
                          :aria-pressed="isExerciseDone(ex.index)" :aria-label="`Mark ${ex.name} complete`"
                          :disabled="selectedIsFuture" @click="onToggleExercise(ex.index)">
                    <span aria-hidden="true">✓</span>
                  </button>
                  <div class="ex-body">
                    <span class="ex-name">{{ ex.name }}</span>
                    <span class="ex-detail">{{ ex.detail }}</span>
                  </div>
                  <button v-if="ex.restSeconds" type="button" class="ex-rest"
                          :disabled="selectedIsFuture" @click="onStartRest(ex.restSeconds, ex.sets)">
                    Rest {{ ex.restSeconds }}s
                  </button>
                </div>
                <NoteField :text="ex.note" :label="ex.name" :disabled="selectedIsFuture" @save="(t) => saveNote({ kind: 'exercise', name: ex.name }, t)" />
              </li>
            </ul>
          </section>

          <!-- Cool-down phase -->
          <section v-if="cooldownStretches.length" class="phase" :class="{ 'is-done': cooldownDone }">
            <div class="phase__head">
              <h4>Cool-down</h4>
              <button type="button" class="phase__mark" :class="{ 'is-done': cooldownDone }"
                      :aria-pressed="cooldownDone" :disabled="selectedIsFuture" @click="onMarkPhase('cooldown')">
                {{ cooldownDone ? '✓ Complete' : 'Mark Phase Complete' }}
              </button>
            </div>
            <ul class="movement-list">
              <li v-for="(stretch, i) in cooldownStretches" :key="i">
                <span class="movement-list__name">{{ stretch.name }}</span>
                <span v-if="stretch.hold" class="movement-list__detail">{{ stretch.hold }}</span>
              </li>
            </ul>
            <NoteField :text="noteFor({ kind: 'cooldown' })" label="cool-down" :disabled="selectedIsFuture" @save="(t) => saveNote({ kind: 'cooldown' }, t)" />
          </section>
        </template>

        <!-- Day-level note + overall completion -->
        <div class="training-day__note">
          <span class="training-day__note-label">Day note</span>
          <NoteField :text="noteFor({ kind: 'day' })" label="this day" :disabled="selectedIsFuture" @save="(t) => saveNote({ kind: 'day' }, t)" />
        </div>
        <button type="button" class="btn btn-green training-day__complete" :class="{ 'is-done': dayDone }"
                :disabled="selectedIsFuture" @click="onMarkDay">
          <span v-if="dayCompletePulse" class="training-day__complete-burst" aria-hidden="true" />
          {{ dayDone ? '✓ Day Complete — tap to undo' : 'Mark Day Complete' }}
        </button>
      </div>

      <details class="training-preview">
        <summary>
          <span class="training-preview__title">Plan Preview</span>
          <span class="training-preview__hint training-preview__hint--expand">tap to expand</span>
          <span class="training-preview__hint training-preview__hint--collapse">tap to collapse</span>
          <span class="training-preview__chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </span>
        </summary>
        <WeekView
          :plan="activePlan"
          :checked="checked"
          :today="todayISO"
          :active-week="position.week"
          :active-day-of-week="position.dayOfWeek"
          @select="setPosition"
        />
      </details>

      <button class="btn btn-secondary training-clear" type="button" @click="clearPlan">Clear Plan</button>
    </section>

    <section v-else-if="view === 'review'" class="card training-review">
      <div class="training-review__top">
        <div>
          <div class="card-title">Review & Adjust</div>
          <p>Confirm the basics before generating from your latest score.</p>
        </div>
        <button class="btn btn-secondary" type="button" @click="backToEmpty">Back</button>
      </div>

      <div v-if="!hasScore && !isPregnancyMode" class="alert alert-warn">
        No score calculated yet. Go to Dashboard to record a test or use your latest saved score, then return to Training.
      </div>

      <div class="training-form">
        <div class="field">
          <label>Official Test Date</label>
          <input v-model="testDate" type="date" />
          <div class="training-form__hint">
            <template v-if="daysUntilTest !== null">{{ daysUntilTest }} day{{ daysUntilTest !== 1 ? 's' : '' }} until test</template>
          </div>
        </div>

        <div class="field">
          <label>Plan Start Date</label>
          <input v-model="planStartDate" type="date" :min="planStartMin" />
          <div class="training-form__hint">
            <template v-if="planStartRange">{{ planStartRange }}</template>
          </div>
        </div>

        <div class="field">
          <label>Focus Area</label>
          <select v-model="focus">
            <option value="balanced">Balanced</option>
            <option value="cardio">Cardio heavy</option>
            <option value="strength">Strength heavy</option>
            <option value="body">Body composition</option>
          </select>
        </div>

        <div class="field">
          <label>Fitness Level</label>
          <select v-model="level">
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        <div class="field">
          <label>Days per week</label>
          <select v-model="daysPerWeek">
            <option :value="3">3 days/week</option>
            <option :value="4">4 days/week</option>
            <option :value="5">5 days/week</option>
            <option :value="6">6 days/week</option>
          </select>
        </div>

        <div class="field training-form__wide">
          <label>Injuries or limitations (optional)</label>
          <textarea v-model="injuries" placeholder="e.g. knee pain, lower back issues, shoulder limitation..." rows="2"></textarea>
        </div>

        <div class="field training-form__wide">
          <label>Equipment available</label>
          <select v-model="equipment">
            <option value="bodyweight">Bodyweight only</option>
            <option value="gym">Gym + bodyweight</option>
          </select>
        </div>

        <div class="field training-form__wide">
          <label>Pregnancy / postpartum</label>
          <select v-model="pregnancySelection">
            <option value="none">Not applicable</option>
            <option value="pregnancy">Pregnancy or postpartum, phase not listed</option>
            <option value="t1">1st Trimester</option>
            <option value="t2">2nd Trimester</option>
            <option value="t3">3rd Trimester</option>
            <option value="postpartum-standard">Postpartum - Standard Delivery</option>
            <option value="postpartum-csection">Postpartum - C-Section</option>
          </select>
        </div>
      </div>

      <div v-if="isLoading" class="ai-build" role="status" aria-live="polite">
        <div class="ai-build__mark" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div class="ai-build__body">
          <div class="ai-build__eyebrow">AI plan build</div>
          <div class="ai-build__title">Building your workout plan</div>
          <div class="ai-build__meta">{{ currentModel || 'Selecting model' }}</div>
          <div class="ai-build__track" aria-hidden="true"><span /></div>
          <div class="ai-build__steps" aria-hidden="true">
            <span v-for="stage in aiBuildStages" :key="stage">{{ stage }}</span>
          </div>
        </div>
      </div>

      <div v-if="status === 'error'" class="alert alert-error">{{ errorMsg }}</div>
      <div v-if="status === 'success'" class="alert alert-info">{{ planSummary || 'Plan generated.' }}</div>

      <button class="btn btn-primary training-generate" data-onboarding="training-generate" type="button" :disabled="isLoading || (!hasScore && !isPregnancyMode)" @click="onGenerate">
        {{ generateLabel }}
      </button>
    </section>

    <section v-else class="card training-empty">
      <div class="card-title">Training</div>
      <h2>No active training plan</h2>
      <p>Generate a plan from your latest score, or import a saved plan JSON.</p>
      <div class="training-empty__actions">
        <button class="btn btn-primary" type="button" @click="openReview">Generate Plan from Latest Score</button>
        <button class="btn btn-secondary" type="button" @click="importFileEl?.click()">Import Plan</button>
      </div>
      <div v-if="importError" class="alert alert-error">{{ importError }}</div>
    </section>

    <!-- No id here on purpose: the Training import triggers via the Vue ref
         (importFileEl). The legacy `#plan-import-file` stays owned by the old
         AI Plan island until those panels are retired, so we avoid a duplicate
         DOM id. -->
    <input
      ref="importFileEl"
      type="file"
      accept=".json,application/json"
      style="display:none;"
      @change="handleImportFile"
    />
  </div>
</template>

<style scoped>
.training-shell {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.training-empty,
.training-review,
.training-active {
  min-height: 240px;
  width: 100%;
}
.training-active {
  padding: 12px;
}
.training-empty h2,
.training-active h2,
.training-day h3 {
  margin: 0;
  color: var(--text);
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 28px;
  line-height: 1.05;
  text-transform: uppercase;
}
.training-empty p,
.training-review p,
.training-active p,
.training-day p {
  margin: 8px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.45;
}
.training-empty__actions,
.training-review__top,
.training-active__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: nowrap;
}
.training-active__date {
  flex: 0 1 auto;
  min-width: 0;
  color: var(--accent);
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  line-height: 1.1;
  text-align: right;
  text-transform: uppercase;
}
.training-empty__actions {
  margin-top: 18px;
}
.training-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}
.training-form__wide {
  grid-column: 1 / -1;
}
.training-form__hint {
  min-height: 16px;
  margin-top: 5px;
  color: var(--muted);
  font-size: 11px;
}
.training-generate {
  width: 100%;
  margin-top: 14px;
  min-height: 46px;
}
.ai-build {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  margin-top: 16px;
  padding: 14px;
  border: 1px solid rgba(201, 154, 73, 0.38);
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(201, 154, 73, 0.12), rgba(31, 122, 79, 0.08)),
    var(--bg);
  overflow: hidden;
}
.ai-build__mark {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: end;
  gap: 4px;
  width: 52px;
  height: 52px;
  padding: 11px;
  border: 1px solid var(--border2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}
.ai-build__mark span {
  display: block;
  width: 100%;
  height: 18px;
  border-radius: 3px;
  background: var(--accent);
  transform-origin: bottom;
  animation: ai-build-bars 1.05s ease-in-out infinite;
}
.ai-build__mark span:nth-child(2) {
  height: 27px;
  animation-delay: 0.14s;
}
.ai-build__mark span:nth-child(3) {
  height: 22px;
  animation-delay: 0.28s;
}
.ai-build__body {
  min-width: 0;
}
.ai-build__eyebrow {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--accent);
}
.ai-build__title {
  margin-top: 2px;
  color: var(--text);
  font-weight: 700;
  line-height: 1.25;
}
.ai-build__meta {
  margin-top: 3px;
  color: var(--muted);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ai-build__track {
  position: relative;
  height: 5px;
  margin-top: 11px;
  border-radius: 999px;
  background: var(--border2);
  overflow: hidden;
}
.ai-build__track span {
  position: absolute;
  inset: 0 auto 0 0;
  width: 42%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--green), var(--accent));
  animation: ai-build-scan 1.2s cubic-bezier(0.65, 0, 0.35, 1) infinite;
}
.ai-build__steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin-top: 9px;
}
.ai-build__steps span {
  min-width: 0;
  color: var(--muted);
  font-size: 10px;
  line-height: 1.1;
  text-transform: uppercase;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.training-shell :deep(input),
.training-shell :deep(select),
.training-shell :deep(button) {
  font-size: 16px;
}
.training-clear {
  width: 100%;
  min-height: 40px;
  margin-top: 10px;
}
.training-day {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.training-day__kicker {
  color: var(--accent);
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
}
.training-preview-note {
  padding: 8px 10px;
  border: 1px solid rgba(201, 154, 73, 0.42);
  border-radius: 6px;
  color: var(--accent);
  background: rgba(201, 154, 73, 0.08);
  font-size: 12px;
}
.training-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}
.training-progress__bar {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: var(--border2);
  overflow: hidden;
}
.training-progress__bar span {
  display: block;
  height: 100%;
  background: var(--green);
  border-radius: 3px;
  transition: width 0.35s ease;
}
.training-progress__text {
  flex-shrink: 0;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--muted);
}
.training-rest {
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg);
}
.training-rest__text {
  margin: 0 !important;
  color: var(--text) !important;
  font-size: 14px !important;
  line-height: 1.5;
}
.phase {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px;
  background: var(--bg);
}
.phase.is-done {
  border-color: var(--green);
}
.phase__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.phase__head h4 {
  margin: 0;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 16px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--text);
}
.phase__mark {
  min-height: 30px;
  padding: 4px 9px;
  border: 1px solid var(--border2);
  border-radius: 6px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
}
.phase__mark:disabled {
  cursor: default;
  opacity: 0.48;
}
.phase__mark.is-done {
  border-color: var(--green);
  color: var(--green);
}
.movement-list {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.movement-list li {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
  color: var(--text);
}
.movement-list__detail {
  flex-shrink: 0;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.exercise-list {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.exercise-list li {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.ex-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ex-check {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border: 2px solid var(--border2);
  border-radius: 7px;
  background: transparent;
  color: transparent;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}
.ex-check:disabled {
  cursor: default;
  opacity: 0.5;
}
.ex-check.is-done {
  border-color: var(--green);
  background: var(--green);
  color: #04110a;
}
.ex-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.ex-name {
  color: var(--text);
  font-size: 13px;
  line-height: 1.25;
}
.exercise-list li.is-done .ex-name {
  text-decoration: line-through;
  opacity: 0.55;
}
.ex-detail {
  color: var(--muted);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.ex-rest {
  flex-shrink: 0;
  min-height: 32px;
  padding: 4px 9px;
  border: 1px solid var(--accent);
  border-radius: 6px;
  background: transparent;
  color: var(--accent);
  cursor: pointer;
  font-size: 12px;
}
.ex-rest:disabled {
  cursor: default;
  opacity: 0.48;
}
.training-day__note {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.training-day__note-label {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--muted);
}
.training-day__complete {
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: 42px;
  margin-top: 2px;
}
.training-day__complete.is-done {
  opacity: 0.9;
}
.training-day__complete-burst {
  position: absolute;
  inset: -50% auto -50% -30%;
  width: 34%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.42), transparent);
  transform: skewX(-18deg);
  animation: day-complete-sheen 0.75s ease-out both;
}
.training-day__complete-burst::after {
  content: '';
  position: absolute;
  top: 50%;
  right: -10px;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.68);
  transform: translateY(-50%);
  box-shadow: 0 0 18px rgba(255, 255, 255, 0.55);
}
.training-day__complete:disabled {
  cursor: default;
  opacity: 0.5;
}
.training-preview {
  margin-top: 12px;
  border-top: 1px solid var(--border);
  padding-top: 10px;
}
.training-preview summary {
  min-height: 38px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--accent);
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  cursor: pointer;
  list-style: none;
}
.training-preview summary::-webkit-details-marker {
  display: none;
}
.training-preview__title {
  flex: 1 1 auto;
  min-width: 0;
}
.training-preview__hint {
  flex: 0 0 auto;
  color: var(--muted);
  font-size: 11px;
  letter-spacing: 0.9px;
}
.training-preview__hint--collapse {
  display: none;
}
.training-preview__chevron {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 1px solid var(--border2);
  border-radius: 999px;
  color: var(--accent);
  line-height: 1;
  transform: rotate(0deg);
  transition: transform 180ms ease, border-color 180ms ease, color 180ms ease;
}
.training-preview__chevron svg {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.training-preview[open] summary {
  margin-bottom: 8px;
}
.training-preview[open] .training-preview__chevron {
  border-color: rgba(201, 154, 73, 0.5);
  color: var(--text);
  transform: rotate(90deg);
}
.training-preview[open] .training-preview__hint--expand {
  display: none;
}
.training-preview[open] .training-preview__hint--collapse {
  display: inline;
}

@media (max-width: 620px) {
  .training-form {
    grid-template-columns: 1fr;
  }
  .training-empty__actions .btn,
  .training-review__top .btn,
  .training-active__top .btn {
    width: 100%;
  }
  .ai-build {
    grid-template-columns: 44px minmax(0, 1fr);
    padding: 12px;
  }
  .ai-build__mark {
    width: 44px;
    height: 44px;
    padding: 9px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-build__mark span,
  .ai-build__track span,
  .training-day__complete-burst {
    animation: none;
  }
  .ai-build__track span {
    width: 100%;
  }
}

@keyframes ai-build-bars {
  0%, 100% { transform: scaleY(0.58); opacity: 0.68; }
  45% { transform: scaleY(1); opacity: 1; }
}

@keyframes ai-build-scan {
  0% { transform: translateX(-105%); }
  100% { transform: translateX(245%); }
}

@keyframes day-complete-sheen {
  0% { transform: translateX(0) skewX(-18deg); opacity: 0; }
  18% { opacity: 1; }
  100% { transform: translateX(410%) skewX(-18deg); opacity: 0; }
}
</style>
